/**
 * builderCatalogApi (feature 0018 M2) — the typed READ client for the seller's catalog, used to bind a
 * REAL product into the builder's product-template preview. Reuses the authed `apiRequest` (Bearer from
 * the JWT; `store_id` is derived server-side — there is no tenant surface here). Read-only.
 */
import type { ProductActiveOffer, StorefrontProduct } from '@aquilixshop/ui-react';
import { buildActiveOfferFromOffer } from '@aquilixshop/ui-react';
import { apiRequest } from './http';
import { NetworkError, SessionExpiredError } from './errors';
import type { PickerItem, PickerSource } from '../inspector/controls/EntityPickerListbox';
import { createCachedPickerSource } from './pickerCache';
import { peekPickedItem } from './pickedProductCache';

/** Lean product list row (mirrors backend `ProductListItemDto`) — for the preview selector. */
export interface ProductListItem {
  id: string;
  name: string;
  slug: string;
  status: string;
  coverImageUrl: string | null;
}

interface MoneyDto {
  amount: number;
  currency: string;
}
interface ImageDto {
  url: string;
  alt: string;
  isCover: boolean;
  /** The option value this photo belongs to ("Bleu"); null for a photo of the product as a whole. */
  optionValue: string | null;
  /** `true` = a photo the seller keeps in her dashboard. The ADMIN read carries it; the canvas must not. */
  internalOnly?: boolean;
  /**
   * `'image'` (absent = image) or `'video'` — a clip the seller dropped in the SAME grid as her photos
   * (20/09/2026). The backend's storefront projection splits it out of `images` into `media`; the canvas
   * must do the same, or a `.mp4` lands in an `<img>` and the builder paints a grey frame + a broken thumb.
   */
  kind?: 'image' | 'video';
  /** For a video: the still extracted at upload (`…-poster.webp`); null when nothing could be extracted. */
  posterUrl?: string | null;
  /** For a video: its delivery ladder (21/09/2026) — the canvas hands it to the same engine the shop uses. */
  sources?: { url: string; width: number | null; height: number | null }[] | null;
}

/** A clip, never to be handed to an `<img>` — same boundary as the backend's `isVideoMedia()`. */
const isVideo = (i: ImageDto): boolean => i.kind === 'video';
interface OptionValueDto {
  value: string;
  swatch: string | null;
}

/**
 * The admin read has no per-value image field (the STOREFRONT projection derives one server-side). The
 * builder derives the same thing from the product's tagged images, so the canvas paints the photo swatches
 * the live storefront paints — the whole point of the preview.
 */
function firstImageUrlFor(images: readonly ImageDto[], value: string): string | null {
  return (
    images.find((i) => !isVideo(i) && i.optionValue?.toLowerCase() === value.toLowerCase())?.url ??
    null
  );
}
interface OptionDto {
  name: string;
  values: OptionValueDto[];
}
/**
 * One REAL variant of the admin `ProductResponse` (backend `VariantDto`). `optionValues` carries one value
 * per product option, IN OPTION ORDER ("Rouge", "M") — the storefront's canonical variant `label` is that
 * tuple joined with " / " (backend `StorefrontCatalogService.toStorefrontView`).
 */
interface VariantDto {
  id: string;
  optionValues: string[];
  price: MoneyDto;
  sku: string | null;
  inventory: { tracked: boolean; quantity: number | null };
  continueSelling: boolean;
}

/** The `ProductResponse` fields we map to a StorefrontProduct (a subset of the backend DTO). */
export interface ProductResponse {
  id: string;
  name: string;
  slug: string;
  description: string;
  status: string;
  price: MoneyDto;
  compareAtPrice: MoneyDto | null;
  inventory: { tracked: boolean; quantity: number | null };
  brand: { id: string; name: string } | null;
  tags: string[];
  images: ImageDto[];
  seo: { metaTitle: string | null; metaDescription: string | null };
  options: OptionDto[];
  variants: VariantDto[];
  /**
   * The product-page template this product actually renders (`null` = the base `default` page). The builder
   * reads it to tell the seller whether the layout on screen is the one this product will really show —
   * previewing a product is NOT assigning it.
   */
  templateSuffix?: string | null;
}

interface ProductListResponse {
  items: ProductListItem[];
  nextCursor: string | null;
}

/** One tier of a RAW quantity offer (backend admin shape) — the fields the builder maps to a render tile. */
export interface QuantityOfferTierResponse {
  id: string;
  minQty: number;
  kind: 'PERCENT' | 'FIXED_AMOUNT' | 'FIXED_PRICE';
  value: number;
  label: string;
  badge: string;
  preselect: boolean;
  hideComparison: boolean;
  plaqueText: string;
  plaqueTextColor: string;
  plaqueBgColor: string;
  priceColor: string;
  bgColor: string;
  borderColor: string;
}

/**
 * `GET /api/quantity-offers?productId=` — a product's single active quantity offer in the RAW authoring shape
 * (pre-label-formatting). The endpoint 404s when the product has no offer; {@link catalogApi.getActiveOffer}
 * maps that (and any failure) to `null` so the builder preview fails open to the classic quantity stepper.
 */
export interface QuantityOfferResponse {
  id: string;
  productId: string;
  enabled: boolean;
  title: string;
  /** The offer's own tile image (feature 0069); null = the product photo. */
  imageUrl?: string | null;
  /** The tile design (28/09/2026): classic | tab | unit | columns | compact; absent = classic. */
  template?: string | null;
  /** Where the per-unit rows take their photos (28/09/2026): variant | custom | none; absent = variant. */
  unitImageMode?: string | null;
  /** The offer's own small variant photos for this product, keyed by option value; `custom` mode only. */
  unitImages?: { value: string; imageUrl: string }[] | null;
  currency: string;
  version: number;
  tiers: QuantityOfferTierResponse[];
}

/** `GET /api/products/template-usage` — product counts per product-page template handle. */
export interface TemplateUsageResponse {
  counts: Record<string, number>;
}

/** `PATCH /api/products/{id}/template` — the stored assignment (null = back to the base `default` page). */
export interface SetTemplateResponse {
  templateSuffix: string | null;
}

const PRODUCTS_PATH = '/api/products';
const QUANTITY_OFFERS_PATH = '/api/quantity-offers';
const REVIEWS_PREVIEW_PATH = '/api/store/apps/reviews/storefront-preview';

/**
 * WHY the reviews preview did not come back — the part the canvas has to be able to say out loud.
 * Three causes, because each one has a DIFFERENT fix for the seller: the backend is unreachable, the
 * backend answered an error, or her session ended. "Unknown error" would be one sentence that helps with
 * none of them.
 */
export type PreviewReviewsFailure = 'offline' | 'server' | 'session';

/**
 * The three things the preview call can come back with. `none` is an ANSWER (this product has no
 * published review, cache it); `failed` is the ABSENCE of one (never cache it, never print it as «0 avis»).
 */
export type PreviewReviewsAnswer =
  | { readonly status: 'ok'; readonly payload: unknown }
  | { readonly status: 'none' }
  | { readonly status: 'failed'; readonly reason: PreviewReviewsFailure };

export const catalogApi = {
  /** GET /api/products — the first page of the seller's products (lean), for the preview selector. */
  async listProducts(): Promise<ProductListItem[]> {
    const res = await apiRequest<ProductListResponse>(PRODUCTS_PATH, { query: { limit: '100' } });
    return res.items;
  },

  /** GET /api/products/{id} — a full product to bind into the preview render context. */
  async getProduct(id: string): Promise<ProductResponse> {
    return apiRequest<ProductResponse>(`${PRODUCTS_PATH}/${encodeURIComponent(id)}`);
  },

  /**
   * GET /api/products/template-usage — how many products render each product-page template, keyed by handle
   * (no-suffix products counted under `default`). Store-wide, so it is NOT derivable from the builder's
   * catalog cache, which is capped at the first 100 products.
   *
   * Returns `null` on ANY failure — deliberately NOT `{}`. The server omits zero-count handles, so an empty
   * map means "every template is unused", and rendering that from a failed request would tell the seller
   * their layouts reach nothing. Silence is the only honest failure mode here.
   */
  async templateUsage(): Promise<Record<string, number> | null> {
    try {
      const res = await apiRequest<TemplateUsageResponse>(`${PRODUCTS_PATH}/template-usage`);
      return res.counts ?? {};
    } catch {
      return null;
    }
  },

  /**
   * PATCH /api/products/{id}/template — point a product at a page template. `null` clears the assignment
   * back to the base `default` page. Throws on failure (422 for a draft-only/unknown handle): the caller
   * surfaces it, because an assignment that silently failed is exactly the confusion this action removes.
   */
  async assignTemplate(productId: string, templateSuffix: string | null): Promise<void> {
    await apiRequest<SetTemplateResponse>(
      `${PRODUCTS_PATH}/${encodeURIComponent(productId)}/template`,
      { method: 'PATCH', body: { templateSuffix } },
    );
  },

  /**
   * GET /api/quantity-offers?productId= — the product's single active quantity offer (raw authoring shape), or
   * `null` when it has none (404) OR on ANY failure. FAIL-OPEN by design (feature 0026-07): the builder preview
   * then falls back to the classic quantity stepper and never crashes the canvas. Same auth / api-base / tenancy
   * (store from the JWT) as {@link getProduct} — the builder never sends a store id.
   */
  async getActiveOffer(productId: string): Promise<QuantityOfferResponse | null> {
    try {
      return await apiRequest<QuantityOfferResponse>(QUANTITY_OFFERS_PATH, {
        query: { productId },
      });
    } catch {
      // 404 (no offer), any non-2xx, session-expired, or a transport failure → no preview offer (stepper).
      return null;
    }
  },

  /**
   * GET /api/store/apps/reviews/storefront-preview?productId= — the product's published reviews in the
   * STOREFRONT's own shape, so the canvas paints what the shop will paint (feature 0066).
   *
   * The canvas used to carry no reviews at all: the `product-reviews` section printed its "0 reviews"
   * editing placeholder on every store, including ones with four hundred, and a seller who had just
   * imported a file concluded the import had failed. This is that hole closed — one call, the storefront's
   * own projection, no second definition of "which reviews" to drift from the page.
   *
   * It is STILL fail-open — a preview is never worth a broken canvas — but it no longer fails SILENTLY.
   * It used to collapse "204, this product has no published review" and "the call blew up" into the same
   * `null`, so a dead backend, an expired session and an empty product all painted the very same sentence:
   * «0 avis». The seller then read a failure as a fact about her shop, and, because the store CACHES the
   * answer for the session, the lie held until she reloaded the whole editor (founder, 18/09/2026 — the
   * local backend had been down since 20h40). Three outcomes now travel separately, and the one that is
   * not an answer says why (same rule as the import banners: a failure names its cause and its fix).
   */
  async getStorefrontReviews(productId: string): Promise<PreviewReviewsAnswer> {
    try {
      const body = await apiRequest<unknown>(REVIEWS_PREVIEW_PATH, { query: { productId } });
      // 204 → `apiRequest` returns undefined. THAT is the real "this product has no published review".
      return body === undefined || body === null
        ? { status: 'none' }
        : { status: 'ok', payload: body };
    } catch (err) {
      if (err instanceof NetworkError) return { status: 'failed', reason: 'offline' };
      if (err instanceof SessionExpiredError) return { status: 'failed', reason: 'session' };
      return { status: 'failed', reason: 'server' };
    }
  },
};

/**
 * Map a bound preview product + its RAW active offer to the render {@link ProductActiveOffer} the buy-box
 * `quantity` block reads (feature 0026-07). Each tile's pack price labels are computed from the product's
 * DEFAULT-variant unit price — sourced from the builder's `StorefrontProduct.price` (cents), which
 * {@link productResponseToStorefront} carries verbatim from the `ProductResponse` base price — via the SAME
 * shared engine the storefront/backend use, so the preview tiles match the live storefront. The result's
 * `productId` is forced to the BOUND product's id so the block's `offer.productId === product.id` gate passes.
 * Returns `null` when there is no offer, it is disabled, or it has no tiers (→ the classic stepper).
 */
export function activeOfferForProduct(
  product: StorefrontProduct,
  offer: QuantityOfferResponse | null,
): ProductActiveOffer | null {
  if (!offer || !offer.enabled || offer.tiers.length === 0) return null;
  return buildActiveOfferFromOffer(
    { ...offer, productId: product.id },
    product.price.amount,
    product.price.currency,
  );
}

// ── Product picker source (feature 0023 P1) ──────────────────────────────────
// The searchable `product-picker` inspector control (ProductControl) fetches candidates by SERVER search
// (never a client filter of one big page, which would hide older products) and resolves the current
// selection by id for the trigger. Only ACTIVE products are selectable (a DRAFT binding resolves to
// nothing on the storefront — spec §5.2 / m6).

const PICKER_LIMIT = '50';

/** The picker thumbnail = the first PUBLIC photo, the one the shop shows. */
function coverOf(images: ImageDto[]): string | null {
  return images.find((i) => !i.internalOnly && !isVideo(i))?.url ?? null;
}

/** Raw server search (no cache) — the ACTIVE products matching `term`, mapped to picker rows. */
async function searchProductsForPicker(term: string): Promise<PickerItem[]> {
  // `status=ACTIVE` filters server-side (the endpoint supports it), so pagination is not skewed by a
  // client filter and DRAFT products (which would resolve to nothing on the storefront) never appear.
  const query: Record<string, string> = { limit: PICKER_LIMIT, status: 'ACTIVE' };
  if (term) query.search = term;
  const res = await apiRequest<ProductListResponse>(PRODUCTS_PATH, { query });
  return res.items.map((p) => ({ id: p.id, name: p.name, thumbnailUrl: p.coverImageUrl }));
}

/** Raw single-product fetch — the fallback the picker cache uses only for a selection beyond page 1. */
async function fetchProductForPicker(id: string): Promise<PickerItem | null> {
  const p = await catalogApi.getProduct(id);
  return { id: p.id, name: p.name, thumbnailUrl: coverOf(p.images) };
}

/**
 * Stable (module-level) picker source for `ProductControl`, wrapped in the shared client cache so the
 * many pickers a section like Bundle deals renders SHARE one request, reopen instantly, and resolve a
 * trigger label from the loaded list instead of a per-product GET. See {@link createCachedPickerSource}.
 *
 * `peek` reaches into the canvas's cross-reload snapshot of resolved products, so on a COLD load the
 * inspector names the bound product on the first frame instead of showing "…" while the list request flies.
 */
export const productPickerSource: PickerSource = createCachedPickerSource({
  search: searchProductsForPicker,
  fetchOne: fetchProductForPicker,
  peek: peekPickedItem,
});

/**
 * Map the admin {@link ProductResponse} to the storefront-render {@link StorefrontProduct} (feature 0018
 * M2). The aggregate `rating` has no admin source, so it is null; `vendor` comes from the brand name; the
 * option values' `value` becomes the storefront option `label`.
 */
export function productResponseToStorefront(p: ProductResponse): StorefrontProduct {
  // The admin read carries the seller's dashboard-only photos; the live storefront DTO drops them. Drop
  // them HERE too, before anything else reads `images` — otherwise the canvas shows a gallery, a swatch or
  // a cover the shop will never show, which is the one thing the preview exists to prevent.
  //
  // The same split the backend's `toStorefrontView` makes (20/09/2026): `media` is the seller's whole
  // gallery in her order, `images` is the PHOTOS only — the list every `<img>` (cover, swatches, cards)
  // reads. Before this split the canvas put the clip's `.mp4` in an `<img>`: grey frame, broken thumbnail.
  const media = p.images.filter((i) => !i.internalOnly);
  const images = media.filter((i) => !isVideo(i));
  return {
    id: p.id,
    name: p.name,
    slug: p.slug,
    description: p.description,
    status: p.status,
    price: { amount: p.price.amount, currency: p.price.currency },
    compareAtPrice: p.compareAtPrice
      ? { amount: p.compareAtPrice.amount, currency: p.compareAtPrice.currency }
      : null,
    images: images.map((i, idx) => ({
      url: i.url,
      alt: i.alt,
      // Re-derived on what REMAINS, like the storefront DTO: the first PUBLIC photo is the cover.
      isCover: idx === 0,
      optionValue: i.optionValue ?? null,
    })),
    media: media.map((i) => ({
      url: i.url,
      alt: i.alt,
      kind: isVideo(i) ? ('video' as const) : ('image' as const),
      posterUrl: i.posterUrl ?? null,
      optionValue: i.optionValue ?? null,
      sources: (i.sources ?? []).map((r) => ({ url: r.url, width: r.width, height: r.height })),
    })),
    inventory: { tracked: p.inventory.tracked, quantity: p.inventory.quantity },
    seo: { metaTitle: p.seo.metaTitle, metaDescription: p.seo.metaDescription },
    tags: p.tags,
    vendor: p.brand?.name ?? null,
    rating: null,
    options: p.options.map((o) => ({
      name: o.name,
      values: o.values.map((v) => ({
        label: v.value,
        swatch: v.swatch,
        imageUrl: firstImageUrlFor(images, v.value),
      })),
    })),
    // Carry the REAL variants down, exactly like the storefront DTO mapping does. WITHOUT them the picker
    // falls back to `variantsFromOptions`, which derives display variants from the FIRST option only — a
    // Couleur × Taille product then showed a single row in the builder while the live storefront showed
    // both. The canonical label is the option-value tuple joined " / " (the shape multi-option mode parses).
    variants: (p.variants ?? []).map((v) => ({
      id: v.id,
      label: v.optionValues.join(' / '),
      price: { amount: v.price.amount, currency: v.price.currency },
      // Same rule as the backend's storefront projection: untracked ⇒ always available.
      available: v.inventory.tracked ? (v.inventory.quantity ?? 0) > 0 || v.continueSelling : true,
      sku: v.sku,
    })),
  };
}
